/**
 * Shared utilities for AWS Textract OCR processing
 */

import { datadogLogs } from "@datadog/browser-logs";

export interface BoundingBox {
  Top: number;
  Left: number;
  Width: number;
  Height: number;
}

interface TextractBlock {
  BlockType?: string;
  Query?: { Text: string };
  Id?: string;
  Text?: string;
  Relationships?: Array<{ Type: string; Ids?: string[] }>;
  Confidence?: number;
  Geometry?: { BoundingBox?: BoundingBox };
}

interface TextractResponse {
  Blocks?: TextractBlock[];
}

interface AnalyzeIdResponse {
  response: {
    IdentityDocuments?: Array<{
      IdentityDocumentFields?: Array<{
        Type?: { Text: string };
        ValueDetection?: { Text: string; Confidence: number };
      }>;
    }>;
  };
}

interface AnalyzeDocumentResponse {
  response: TextractResponse;
}

/**
 * Upload base64 image to OCR API endpoint for AnalyzeID
 */
export async function analyzeIdImage(
  imageBase64: string,
): Promise<AnalyzeIdResponse> {
  const formData = new FormData();
  formData.append("file", imageBase64);

  const response = await fetch("/api/ocr/analyze-id", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`OCR AnalyzeID failed: ${response.statusText}`);
  }

  return response.json() as Promise<AnalyzeIdResponse>;
}

/**
 * Upload base64 image to OCR API endpoint for AnalyzeDocument with queries
 */
export async function analyzeDocumentImage(
  imageBase64: string,
  queries: string[],
): Promise<AnalyzeDocumentResponse> {
  const formData = new FormData();
  formData.append("file", imageBase64);
  formData.append("queries", JSON.stringify(queries));

  const response = await fetch("/api/ocr/analyze-document", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`OCR AnalyzeDocument failed: ${response.statusText}`);
  }

  return response.json() as Promise<AnalyzeDocumentResponse>;
}

/**
 * Calculate average confidence from Textract blocks
 * IMPORTANT: Only counts blocks with actual text content (not empty strings)
 */
export function calculateConfidence(
  blocks: TextractBlock[] | Array<{ Confidence?: number; Text?: string }>,
): number {
  if (!blocks || blocks.length === 0) return 0;

  // Filter for blocks with confidence AND non-empty text
  const confidenceValues = blocks
    .filter((block) => {
      // Must have confidence score
      if (block.Confidence === undefined) return false;

      // If block has Text property, it must be non-empty
      if ("Text" in block && typeof block.Text === "string") {
        return block.Text.trim().length > 0;
      }

      // For blocks without Text property, include them
      return true;
    })
    .map((block) => block.Confidence as number);

  if (confidenceValues.length === 0) return 0;

  const sum = confidenceValues.reduce((acc, val) => acc + val, 0);
  return sum / confidenceValues.length / 100; // Convert to 0-1 range
}

/**
 * Check if confidence meets threshold
 */
export function meetsConfidenceThreshold(
  confidence: number,
  threshold = 0.85,
): boolean {
  return confidence >= threshold;
}

/**
 * Log OCR usage for cost tracking
 */
export interface OcrUsageMetadata {
  documentType: "id" | "insurance";
  pageCount: number;
  featureTypes: string[];
  confidence: number;
  success: boolean;
  consumerId?: string;
  orgId?: number;
}

export function logOcrUsage(metadata: OcrUsageMetadata): void {
  datadogLogs.logger.info("OCR usage tracked", {
    ...metadata,
    timestamp: new Date().toISOString(),
    costEstimate: calculateCostEstimate(metadata),
  });
}

/**
 * Estimate OCR cost based on usage
 */
function calculateCostEstimate(metadata: OcrUsageMetadata): number {
  const { documentType, pageCount } = metadata;

  // AWS Textract pricing (per 1000 pages)
  const ANALYZE_ID_COST_PER_1000 = 1.5;
  const ANALYZE_DOCUMENT_QUERIES_COST_PER_1000 = 15.0;

  const costPer1000 =
    documentType === "id"
      ? ANALYZE_ID_COST_PER_1000
      : ANALYZE_DOCUMENT_QUERIES_COST_PER_1000;

  return (pageCount / 1000) * costPer1000;
}

/**
 * Parse Textract AnalyzeID response
 */
export interface ParsedIdData {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  dob: string | null;
  address: {
    street: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
  };
  idNumber: string | null;
  expirationDate: string | null;
  documentNumber: string | null;
}

export function parseAnalyzeIdResponse(
  response: AnalyzeIdResponse["response"],
): ParsedIdData {
  const identityDocuments = response.IdentityDocuments || [];

  if (identityDocuments.length === 0) {
    return createEmptyIdData();
  }

  const idDoc = identityDocuments[0];
  const fields = idDoc.IdentityDocumentFields || [];

  const fieldMap: Record<string, string> = {};
  for (const field of fields) {
    if (field.Type?.Text && field.ValueDetection?.Text) {
      fieldMap[field.Type.Text] = field.ValueDetection.Text;
    }
  }

  return {
    firstName: fieldMap.FIRST_NAME || null,
    lastName: fieldMap.LAST_NAME || null,
    middleName: fieldMap.MIDDLE_NAME || null,
    dob: fieldMap.DATE_OF_BIRTH || null,
    address: {
      street: fieldMap.ADDRESS || null,
      city: fieldMap.CITY_IN_ADDRESS || null,
      state: fieldMap.STATE_IN_ADDRESS || null,
      zipCode: fieldMap.ZIP_CODE_IN_ADDRESS || null,
    },
    idNumber: fieldMap.ID_NUMBER || fieldMap.DOCUMENT_NUMBER || null,
    expirationDate: fieldMap.EXPIRATION_DATE || null,
    documentNumber: fieldMap.DOCUMENT_NUMBER || null,
  };
}

function createEmptyIdData(): ParsedIdData {
  return {
    firstName: null,
    lastName: null,
    middleName: null,
    dob: null,
    address: {
      street: null,
      city: null,
      state: null,
      zipCode: null,
    },
    idNumber: null,
    expirationDate: null,
    documentNumber: null,
  };
}

/**
 * Parse Textract AnalyzeDocument (QUERIES) response into generic key-value map
 * Returns a map of query text to extracted value
 */
export function parseQueryResults(
  response: TextractResponse,
): Record<string, string | null> {
  const blocks = response.Blocks || [];
  const queryResults: Record<string, string | null> = {};

  for (const block of blocks) {
    if (block.BlockType === "QUERY" && block.Query) {
      const queryText = block.Query.Text;

      const blockAnswerId = block.Relationships?.find(
        (rel) => rel.Type === "ANSWER",
      )?.Ids?.[0];

      if (!blockAnswerId) {
        console.warn("No ANSWER relationship found for QUERY block:", block);
        queryResults[queryText] = null;
        continue;
      }

      // Find corresponding QUERY_RESULT
      const resultBlock = blocks.find(
        (b: TextractBlock) =>
          b.BlockType === "QUERY_RESULT" && b.Id === blockAnswerId,
      );

      queryResults[queryText] = resultBlock?.Text || null;
    }
  }

  return queryResults;
}

/**
 * Parse Textract AnalyzeDocument (QUERIES) response into key-value map
 * AND a map of query text to bounding boxes (for field highlighting)
 */
export function parseQueryResultsWithBoundingBoxes(
  response: TextractResponse,
): {
  results: Record<string, string | null>;
  boundingBoxes: Record<string, BoundingBox | null>;
} {
  const blocks = response.Blocks || [];
  const results: Record<string, string | null> = {};
  const boundingBoxes: Record<string, BoundingBox | null> = {};

  for (const block of blocks) {
    if (block.BlockType === "QUERY" && block.Query) {
      const queryText = block.Query.Text;
      const blockAnswerId = block.Relationships?.find(
        (rel) => rel.Type === "ANSWER",
      )?.Ids?.[0];

      if (!blockAnswerId) {
        results[queryText] = null;
        boundingBoxes[queryText] = null;
        continue;
      }

      const resultBlock = blocks.find(
        (b: TextractBlock) =>
          b.BlockType === "QUERY_RESULT" && b.Id === blockAnswerId,
      );

      results[queryText] = resultBlock?.Text || null;
      boundingBoxes[queryText] = resultBlock?.Geometry?.BoundingBox || null;
    }
  }

  return { results, boundingBoxes };
}
